import { Button, Menu } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AutoSaveModal from "src/generator/_Modals/AutoSaveModal/AutoSaveModal";

import { useCheckPerm, useGetApi } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import {
  resetEntryLock,
  setNoSaves,
  setReleaseEntryLock,
} from "src/redux/slices/entrySlice";
import {
  setForceTurnOffInlineSave,
  setIsInitialInsert,
  setNewEntryData,
} from "src/redux/slices/generatorSlice";

import { useClearCursorPositions } from "../../../hooks/useSavedCursorPosition";
import { useClearScrollState } from "../../../hooks/useSavedHorizontalScroll";
import {
  setGuernseyData,
  setNMEAModalData,
} from "../../../redux/slices/generatorFeaturesSlice";
import useResetGuernsey from "../../../components/shared/GuernseyMulti/useResetGuernsey";

interface Props {
  hamburger?: boolean;
  alternativeText?: string;
}

const NewEntryButton = ({ hamburger, alternativeText }: Props) => {
  const lbid = useSelector(state => state.generatorSlice.entryDataToSave.lbid);
  const { entryType } = useSelector(state => state.generatorSlice.lbentry);
  const isV2 = useSelector(
    state => state.generatorSlice.lbentry.generator === "v2",
  );
  const clearScrollsState = useClearScrollState();
  const clearCursorPositions = useClearCursorPositions();
  const { draft, entries } = useSelector(state => state.generatorSlice);
  const [opened, { open, close }] = useDisclosure(false);

  const dispatch = useDispatch();
  const [, setSearchParams] = useSearchParams();
  const checkPerm = useCheckPerm();
  const resetGuernsey = useResetGuernsey();

  const { refetch: fetchNewEntry, isLoading } = useGetApi({
    request: "new-entry",
    body: { params: { lbid } },
    fetchOnInit: false,
  });

  const handleNewEntry = useCallback(
    (data: any) => {
      setSearchParams({ new: "true" }, { replace: true });
      clearScrollsState();
      clearCursorPositions();
      if (data?.data?.lbentry)
        dispatch(
          setNewEntryData({
            lbentry: data.data.lbentry,
          }),
        );
      resetGuernsey();
      dispatch(setReleaseEntryLock(Math.random()));
      dispatch(setNMEAModalData({ opened: true }));
      dispatch(setIsInitialInsert(true));
      dispatch(setForceTurnOffInlineSave(Math.random()));
      dispatch(setGuernseyData(null));
    },
    [dispatch, setSearchParams],
  );

  const parseNew = async (data: any) => {
    if (!draft) {
      handleNewEntry(data);
      if (!isV2) {
        dispatch(resetEntryLock());
      }
    } else {
      open();
    }
  };

  const onNewEntry = useCallback(async () => {
    dispatch(setNoSaves(0));

    const newEntryData = await fetchNewEntry();

    void parseNew(newEntryData);
  }, [draft, fetchNewEntry, dispatch, open, entries]);

  const refetchAndHandle = async () => {
    const newEntryData = await fetchNewEntry();
    handleNewEntry(newEntryData);
  };

  const Component = useCallback(
    () =>
      hamburger ? (
        <Menu.Item onClick={onNewEntry} leftSection={<IconPlus size={20} />}>
          New Entry
        </Menu.Item>
      ) : (
        <Button
          data-cy="new-entry-button"
          variant="outline"
          leftSection={!alternativeText && <IconPlus size={"1rem"} />}
          ml={15}
          loading={isLoading}
          onClick={onNewEntry}>
          {alternativeText ?? "New Entry"}
        </Button>
      ),
    [hamburger, isLoading, onNewEntry],
  );

  if (!checkPerm("createentries") || entryType === "none") {
    return <></>;
  }

  return (
    <>
      <AutoSaveModal
        opened={opened}
        close={close}
        fetchNewEntry={refetchAndHandle}
        dateCreated={draft?.datecreated}
        fullName={draft?.full_name}
      />
      {/*<ExistingEntryModal*/}
      {/*  opened={openExistingEntryModal}*/}
      {/*  close={() => setOpenExistingEntryModal(false)}*/}
      {/*  handleNewEntry={() => parseNew(data)}*/}
      {/*  date={entryModalDate}*/}
      {/*/>*/}
      <Component />
    </>
  );
};

export default NewEntryButton;
